import { useEffect } from 'react';

export const SITE_URL = 'https://hiprotechnologies.com';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export default function Seo({ title, description, path = '/', image, type = 'website', noindex = false, jsonLd }) {
  const jsonLdStr = jsonLd ? JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : '';
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img = image || DEFAULT_OG_IMAGE;
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1');
    upsertMeta('property', 'og:locale', 'en_US');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:site_name', 'Hipro Technologies');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', img);

    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', url);

    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (jsonLdStr) {
      JSON.parse(jsonLdStr).forEach((obj) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-jsonld', '1');
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, path, image, type, noindex, jsonLdStr]);
  return null;
}

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Hipro Technologies',
  slogan: 'Innovate. Build. Scale',
  sameAs: [
    'https://www.instagram.com/hiprotechnologies/',
    'https://www.facebook.com/hiprotechnology',
    'https://www.youtube.com/@HiproTechnologies',
  ],
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  email: 'info@hiprotechnologies.com',
  telephone: '+91-9359819036',
  foundingDate: '2015',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'IT Services',
    itemListElement: [
      'Web Development', 'Mobile App Development', 'Custom Software & SaaS Development',
      'AI & Automation Solutions', 'Cloud Engineering & DevOps', 'Digital Marketing & SEO',
    ].map((s) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: s } })),
  },
  description: 'Hipro Technologies — Innovate. Build. Scale. Software Development, AI Automation & Digital Marketing company with offices in India, USA and UK.',
  address: [
    { '@type': 'PostalAddress', name: 'Corporate Office', streetAddress: '15A, 4th Floor, City Vista, Fountain Road, Kharadi', addressLocality: 'Pune', addressRegion: 'Maharashtra', postalCode: '411014', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: 'Cluster D, Wing 4, EON Free Zone, Kharadi', addressLocality: 'Pune', addressRegion: 'MH', postalCode: '411016', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: '13th Floor, Survey No. 64, Building No. 9, Madhapur', addressLocality: 'Hyderabad', addressRegion: 'Telangana', postalCode: '500081', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: 'Phoenix H10, Tower 2, 6th Floor, Phoenix SEZ Access Road, Phase 2, HITEC City', addressLocality: 'Hyderabad', addressRegion: 'TS', postalCode: '500081', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: '8, Bellary Rd, Ganganagar, Hebbal', addressLocality: 'Bangalore', addressRegion: 'KA', postalCode: '560032', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: 'Cherry Hills, Embassy Golf Links Business Park, Challaghatta, Domlur', addressLocality: 'Bangalore', addressRegion: 'KA', postalCode: '560071', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: 'Cybercity, Phase III', addressLocality: 'Gurugram', addressRegion: 'HR', postalCode: '122002', addressCountry: 'IN' },
    { '@type': 'PostalAddress', streetAddress: '1345 Avenue of the Americas', addressLocality: 'New York', addressRegion: 'NY', postalCode: '10105', addressCountry: 'US' },
    { '@type': 'PostalAddress', streetAddress: '114 Lodge Crescent', addressLocality: 'Waltham Cross', addressRegion: 'England', postalCode: 'EN8 8BJ', addressCountry: 'GB' },
  ],
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Hipro Technologies',
  url: SITE_URL,
};
